// Shapes and words for Driver Refunds (db/migrations/0800, 0801). Every figure is
// computed in Postgres; this file only names the shapes and the labels.
//
// No import from ./loans on purpose: node's test runner resolves imports by exact
// path, and an extensionless re-export stops web/test/driverRefunds.test.ts from
// loading. The page takes the formatters from utils/loans directly.

export type RefundStatus = 'needs_review' | 'raised' | 'verified' | 'approved' | 'rejected' | 'paid'

export const REFUND_STATUS_LABEL: Record<RefundStatus, string> = {
  needs_review: 'Needs review',
  raised: 'Raised, facts to verify',
  verified: 'Verified, to approve',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
}

export const REFUND_STATUS_TONE: Record<RefundStatus, 'green' | 'red' | 'amber' | 'neutral' | 'brand'> = {
  needs_review: 'amber',
  raised: 'amber',
  verified: 'brand',
  approved: 'brand',
  rejected: 'red',
  paid: 'green',
}

export const CAR_OWNER_LABEL: Record<string, string> = {
  own: 'His or her own car',
  family: 'A family member\'s car',
  other_operator: 'Another fleet operator\'s car',
  other: 'Other',
}

export type Money = number | string | null

export type SweepWindow = {
  shift_window: string
  plates: string | null
  trips: number | null
  net_earnings_zar: Money
  cash_collected_zar: Money
  net_paid_to_fleetly_zar: Money
}

export type SweepDay = {
  uber_driver_id: string
  person_id: string | null
  person_name: string
  shift_date: string
  plates: string | null
  windows: number
  trips: number | null
  gross_fares_zar: Money
  cash_collected_zar: Money
  uber_fees_zar: Money
  net_earnings_zar: Money
  net_paid_to_fleetly_zar: Money
  is_negative: boolean | null
  figure_note: string | null
  shift_that_day: string | null
  owner_statements_that_day: string | null
  attribution_note: string | null
  xero_note: string
  bank_letter_verified: boolean
  refund_ref: string | null
  refund_status: RefundStatus | null
  window_rows: SweepWindow[] | null
}

export type SweepPerson = {
  uber_driver_id: string
  person_id: string | null
  person_name: string
  identity_basis: string
  application_status: string | null
  pipeline_stage: string
  ever_a_driver: boolean
  bank_letter_verified: boolean
  first_day: string
  last_day: string
  first_uber_day_on_file: string | null
  days: number
  trips: number | null
  net_paid_to_fleetly_zar: Money
  negative_days: number
  days_without_figure: number
  unrefunded_days: number
  unrefunded_zar: Money
  refund_refs: string[] | null
  attributed_days: number
}

export type Exposure = {
  uber_driver_id: string
  person_name: string
  uber_status: string
  roster_as_of: string | null
  identity_basis: string | null
  driver_status: string
  application_status: string | null
  pipeline_stage: string
  first_uber_day_on_file: string | null
  last_uber_day_on_file: string | null
  uber_days_on_file: number | null
  non_fleet_days: number | null
  link_note: string
}

export type Sweep = {
  ok: true
  as_of: string | null
  totals: {
    people: number
    days: number
    days_without_figure: number
    net_paid_to_fleetly_zar: Money
    positive_zar: Money
    negative_zar: Money
    negative_days: number
    unrefunded_zar: Money
  }
  people: SweepPerson[]
  days: SweepDay[]
  exposure: Exposure[]
}

export type RefundDay = { shift_date: string; net_paid_zar: Money; active: boolean; current_zar: Money; plates: string | null }

export type Refund = {
  id: string
  ref: string
  status: RefundStatus
  // cash_proof: a cash shortfall deducted on a closed payslip, since proven on
  // Driver cash (migration 8831). Its days are the proven shifts.
  source: 'sweep' | 'claim' | 'cash_proof'
  person_id: string
  uber_driver_id: string | null
  person_name: string
  person_email: string | null
  claimed_dates: string[] | null
  claimed_plate: string | null
  car_owner: string | null
  car_owner_note: string | null
  claim_note: string | null
  review_reason: string | null
  amount_zar: Money
  raised_at: string
  raised_by: string | null
  verified_at: string | null
  verified_by: string | null
  verified_by_id: string | null
  verify_note: string | null
  approved_at: string | null
  approved_by: string | null
  rejected_at: string | null
  rejected_by: string | null
  rejection_reason: string | null
  days: RefundDay[] | null
  current_total_zar: Money
  deduction_note?: string | null
  bank_letter_verified: boolean
  payment: null | {
    status: 'staged' | 'paid' | 'cancelled'
    amount_zar: Money
    account_tail: string | null
    bank_key: string | null
    account_holder: string | null
    bank_letter_id: string
    first_day: string
    last_day: string
    days: number
    staged_at: string
    staged_by: string | null
    paid_on: string | null
    paid_by: string | null
    payment_reference: string | null
  }
  messages: { template_key: string; status: string; queued_at: string }[] | null
}

export type RefundList = { ok: true; caps: { can_raise: boolean; can_approve: boolean }; rows: Refund[] }

export type RefundOptions = {
  ok: true
  people: { person_id: string; name: string; uber_driver_id: string }[]
  car_owners: { value: string; label: string }[]
}

/** A negative figure is shown as negative, never folded away. */
export function isNegative(v: Money): boolean {
  if (v === null || v === undefined || v === '') return false
  const n = Number(v)
  return Number.isFinite(n) && n < 0
}

/** Dates typed as "2026-09-25, 2026-09-26" or one per line. */
export function parseDateList(raw: string): { dates: string[]; bad: string[] } {
  const parts = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean)
  const dates: string[] = []
  const bad: string[] = []
  for (const p of parts) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(p) && !Number.isNaN(new Date(`${p}T12:00:00Z`).getTime())) {
      if (!dates.includes(p)) dates.push(p)
    } else {
      bad.push(p)
    }
  }
  return { dates: dates.sort(), bad }
}

/** What the signed-in person can do next on this refund. Postgres decides again. */
export function nextActions(r: Refund, caps: { can_raise: boolean; can_approve: boolean }, me: string | null) {
  const own = me !== null && r.person_id === me
  // 9300: cash proof was checked when its slip was accepted on Driver cash, so it
  // goes from raised straight to an approver, and the maker and checker rule does
  // not apply to it. Verify stays offered for dropping a shift, never required.
  const cash = r.source === 'cash_proof'
  const approvable = r.status === 'verified' || (cash && r.status === 'raised')
  return {
    verify: caps.can_raise && !own && (r.status === 'raised' || r.status === 'verified') && !(cash && caps.can_approve),
    approve: caps.can_approve && !own && approvable && (cash || r.verified_by_id !== me),
    approveBlockedBecauseMaker: caps.can_approve && !cash && r.status === 'verified' && me !== null && r.verified_by_id === me,
    reject: caps.can_approve && (r.status === 'needs_review' || r.status === 'raised' || r.status === 'verified'),
    stage: caps.can_approve && r.status === 'approved' && !r.payment,
    markPaid: caps.can_approve && r.status === 'approved' && r.payment?.status === 'staged',
  }
}
